import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

interface TelegramLoginButtonProps {
  botName: string;
  onAuth: (user: any) => void;
  buttonSize?: 'large' | 'medium' | 'small';
  cornerRadius?: number;
  requestAccess?: boolean;
  lang?: string;
}

const TelegramLoginButton: React.FC<TelegramLoginButtonProps> = ({
  botName,
  onAuth,
  buttonSize = 'large',
  cornerRadius,
  requestAccess = true,
  lang = 'en',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Create global callback function that Telegram will call
    const callbackName = 'onTelegramAuth';
    (window as any)[callbackName] = (user: any) => {
      console.log('✅ Telegram callback received:', user);
      onAuth(user);
    };

    // Load Telegram Widget script if not already loaded
    if (!document.getElementById('telegram-widget-script')) {
      const script = document.createElement('script');
      script.id = 'telegram-widget-script';
      script.src = 'https://telegram.org/js/telegram-widget.js?22';
      script.async = true;
      document.body.appendChild(script);
    }

    // Create the widget element
    if (containerRef.current) {
      // Clear any existing content
      containerRef.current.innerHTML = '';

      // Create script element with data attributes for Telegram Widget
      const widgetScript = document.createElement('script');
      widgetScript.src = 'https://telegram.org/js/telegram-widget.js?22';
      widgetScript.async = true;

      // Configure widget with data attributes
      widgetScript.setAttribute('data-telegram-login', botName);
      widgetScript.setAttribute('data-size', buttonSize);

      // IMPORTANT: Use data-onauth for client-side callback
      // This is more reliable than data-auth-url for embedded widgets
      widgetScript.setAttribute('data-onauth', callbackName + '(user)');

      if (requestAccess) {
        widgetScript.setAttribute('data-request-access', 'write');
      }

      if (cornerRadius !== undefined) {
        widgetScript.setAttribute('data-radius', cornerRadius.toString());
      }

      widgetScript.setAttribute('data-lang', lang);

      console.log('🔧 Initializing Telegram Widget with:', {
        botName,
        callbackName,
        buttonSize,
        requestAccess,
      });

      containerRef.current.appendChild(widgetScript);
    }

    // Cleanup
    return () => {
      delete (window as any)[callbackName];
    };
  }, [botName, onAuth, buttonSize, cornerRadius, requestAccess, lang]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '40px',
      }}
    />
  );
};

export default TelegramLoginButton;
